import { type ISearchSuggestions } from '@/services/models/SearchSuggestions';
import ServerSearchSuggestionsService from '@/services/serverless/ServerSearchSuggestionsService';
import axios, { type AxiosResponse } from 'axios';
import { createAPIClient } from '@/utils/network-utils/createAPIClient';
import {
  AbortedRequestError,
  ResourceNotFoundError,
  UnableToAccessResourceError
} from '@/utils/errors';
import Service from '@/services/Service';
import { EnvironmentService } from '../EnvironmentService';
import type { IGetSuggestionsBySearchTermsParams } from './IGetSuggestionsBySearchTermsParams';
import SearchSuggestionsServiceMock from './SearchSuggestionsServiceMock';

/**
 * An isomorphic service for search suggestions.
 */
export class SearchSuggestionsService extends Service {
  private client = createAPIClient({ baseURL: '/api' });

  /**
   * Get search suggestions by search term.
   *
   * **IMPORTANT**: The {@link ISearchSuggestions} is an aggregation of search suggestions provided by the search
   * engine, together with some {@link IProduct}'s that match the search term.
   *
   * The product suggestions identifiers are provided by the search engine, but the product data is fetched
   * from the {@link ProductService} using the identifiers. The cascade of requests is as follows:
   *
   * 1. The search suggestions are fetched from the search engine using the provided term.
   * 2. The product identifiers are extracted from the search suggestions, together with the suggested terms.
   * 3. The product data is fetched from the {@link ProductService} using the product identifiers.
   * 4. The search suggestions DTO is constructed with the search suggestions and the product data.
   *
   * This have some implications:
   * - The search suggestions DTO may contain products that are not directly related to the search term, but are
   *   suggested by the search engine based on the term.
   * - If the lookup of the product data fails, the search suggestions DTO will still contain the search suggestions,
   *   but the product data that failed will not be included, to avoid breaking the response, or providing data that
   *   may not be accurate (like broken data from the search engine).
   *
   * Also, some performance considerations were taken before implementing this service:
   * - Since we lookup the data, the latency of the request may be higher than expected, especially if the search
   *   suggestions contain a lot of products. This is not the common use case, but if we start considering bigger
   *   suggestions responses, we may need to revisit this implementation.
   * - The products lookups are cached on the server, so the performance impact only exists on the first request. In
   *   real production scenarios, the search suggestions will be cached on the server, so the performance impact
   *   **should** be minimal. In any case, observability here is recommended.
   *
   * If we need to optimize the performance of this service, we can consider:
   * - Caching the search engine responses too if the engine cache itself is not enough
   *   - This might be problematic due to search engines analytics, so beware of that if you decide to implement it.
   * - Streaming the product data as it is fetched, instead of waiting for all products to be fetched before returning.
   *   - To do that, we will need to change the API to support streaming responses or
   *   - Make our {@link withServerFunctionRequest} to support {@link AbortSignal} to enable cancellation of the
   *    request and debouncing.
   *
   * For the initial version, the cache of the products on the server is enough, so we will not implement
   * streaming or caching of the search engine responses in this service for now.
   *
   * @param params - The parameters to search for.
   * @param signal - An optional abort signal to cancel the request.
   * @returns The search suggestions DTOs.
   */
  public async getSuggestionsBySearchTerms(
    { term = '', ...options }: IGetSuggestionsBySearchTermsParams,
    signal?: AbortSignal
  ): Promise<ISearchSuggestions> {
    if ((typeof window === "undefined")) {
      return ServerSearchSuggestionsService.getSuggestionsBySearchTerm(
        term,
        options
      );
    }

    try {
      const { data } = await this.client.post<
        IGetSuggestionsBySearchTermsParams,
        AxiosResponse<ISearchSuggestions>
      >('/search-suggestions', { term, ...options }, { signal });

      return data;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw new AbortedRequestError(
          'The search suggestions request was cancelled by the user',
          { cause: error }
        );
      }

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new ResourceNotFoundError(`Search suggestion data not found`, {
            cause: error
          });
        }
      }

      throw new UnableToAccessResourceError(
        'An unexpected error occurred when retrieving search suggestions',
        { cause: error }
      );
    }
  }
}

const _mock = new SearchSuggestionsServiceMock(SearchSuggestionsService);
export default SearchSuggestionsService.withMock(_mock);
